import React from 'react';
import { Grid, useTheme, useMediaQuery } from '@mui/material';
import HealthStatBox from '../../HealthOverview/components/HealthStatBox';
import { useQuery } from 'react-query';
import useStore from '../../../Store/Store';
import { getHealthDataSync, getUserHealthDataSync } from '../../../utils/api';
import { useTranslation } from 'react-i18next';
const HealthCard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const user = useStore((state) => state.user);
  const userId = user?._id || user?.id;
  const selectedRecipient = useStore((state) => state.selectedRecipient);
  const { t } = useTranslation();
  const { data, isLoading } = useQuery(
    ['getHealthDataSync', userId, selectedRecipient],
    () => {
      if (!selectedRecipient) {
        // If no recipient is selected, get the user's own health data
        return getUserHealthDataSync({ userId });
      } else {
        // If a recipient is selected, get their health data
        return getHealthDataSync({ userId, recipientId: selectedRecipient });
      }
    },
    {
      enabled: !!userId,
      retry: false,
      cacheTime: 0,
    },
  );
  // Fallbacks for loading or missing data
  const health = data?.data || {};

  // Helper to display value or fallback with proper decimal formatting
  const showValue = (val, suffix = '', isCritical = false) => {
    if (val === null || val === undefined) {
      return isCritical ? '--' : `0${suffix}`;
    }

    // Check if the value is a number
    const numValue = Number(val);
    if (isNaN(numValue)) {
      return isCritical ? '--' : `0${suffix}`;
    }

    // Convert meters to kilometers for distance
    let displayValue = numValue;
    if (suffix === ' Km') {
      displayValue = numValue / 1000; // Convert meters to kilometers
    }

    // Format: 2 decimal places for floats, no decimals for integers
    const formattedValue = Number.isInteger(displayValue) ? displayValue.toString() : displayValue.toFixed(2);

    return `${formattedValue}${suffix}`;
  };

  return (
    <Grid item xs={12}>
      <div
        style={{
          background: '#fff',
          borderRadius: '24px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          flexWrap: 'wrap',
        }}
      >
        {isMobile ? (
          <Grid container spacing={2}>
            {[
              { title: t('steps'), value: showValue(health.STEPS, ' steps') },
              { title: t('distance'), value: showValue(health.DISTANCE_TRAVELLED, ' Km') },
              { title: t('calories'), value: showValue(health.CALORIES_BURNED, ' kcal') },
              { title: t('heartRate'), value: showValue(health.HEART_RATE, ' BPM', true) },
              { title: t('sleep'), value: showValue(health.SLEEP, ' minute') },
              { title: t('glucose'), value: showValue(health.GLUCOSE, ' mg/dL', true) },
              {
                title: t('bloodPressure'),
                value: `${showValue(health.BLOOD_PRESSURE_SYSTOLIC, '', true)} / ${showValue(health.BLOOD_PRESSURE_DIASTOLIC, '', true)} mmHg`,
              },
            ].map((stat, idx) => (
              <Grid item xs={6} key={idx}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <HealthStatBox
                    title={stat.title}
                    height="100px"
                    width="110px"
                    value={isLoading ? '...' : stat.value}
                  />
                </div>
              </Grid>
            ))}
          </Grid>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            {[
              { title: t('steps'), value: showValue(health.STEPS, ' steps') },
              { title: t('distance'), value: showValue(health.DISTANCE_TRAVELLED, ' Km') },
              { title: t('calories'), value: showValue(health.CALORIES_BURNED, ' kcal') },
              { title: t('heartRate'), value: showValue(health.HEART_RATE, ' BPM', true) },
              { title: t('sleep'), value: showValue(health.SLEEP, ' minute') },
              { title: t('glucose'), value: showValue(health.GLUCOSE, ' mg/dL', true) },
              {
                title: t('bloodPressure'),
                value: `${showValue(health.BLOOD_PRESSURE_SYSTOLIC, '', true)} / ${showValue(health.BLOOD_PRESSURE_DIASTOLIC, '', true)} mmHg`,
              },
            ].map((stat, idx) => (
              <div key={idx} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <HealthStatBox title={stat.title} height="100px" width="110px" value={isLoading ? '...' : stat.value} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Grid>
  );
};

export default HealthCard;
